import React, { useState, useEffect } from "react";
import { Form, Input, Button, Select, message, Tooltip, Spin } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { InfoCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_API_TENANTS_URL } from "../../../constants";
import { AN_COLORS } from "../../../design-system/tokens";
import { SettingsField, SettingsPage, SettingsSection, SettingsStatus } from "../settingsUi";

const { Option } = Select;

// "SSH Gateway Logs" must match siem.LogTypeSSH in the backend
// (internal/pam/siem/render.go): an output forwards SSH gateway events only
// when its stored, comma-joined log types include exactly this name.
const LOG_TYPES = [
  "Authentication Logs",
  "Database Agents Logs",
  "Platform Audit Logs",
  "RADIUS Logs",
  "SSH Gateway Logs",
  "Workflow Logs",
];

// What each log type sends, and the Splunk sourcetype it arrives under. The
// "coming soon" types are not delivered by the backend yet: they are shown
// disabled, and a saved config that already holds one keeps it (shown
// selected but disabled) rather than dropping it on the next save.
const LOG_TYPE_INFO = {
  "SSH Gateway Logs": { sends: "SSH sessions and decisions", sourcetype: "authnull:ssh" },
  "Database Agents Logs": { sends: "Every database login decision (the Access Logs events)", sourcetype: "authnull:database" },
  "Platform Audit Logs": { sends: "Every console change (the Audit Logs events)", sourcetype: "authnull:audit" },
  "Authentication Logs": { comingSoon: true },
  "RADIUS Logs": { comingSoon: true },
  "Workflow Logs": { comingSoon: true },
};

const isComingSoon = (type) => !!(LOG_TYPE_INFO[type] && LOG_TYPE_INFO[type].comingSoon);

const logTypeOptions = (options) =>
  options.map((o) => ({ value: o, label: isComingSoon(o) ? `${o} (coming soon)` : o, disabled: isComingSoon(o) }));

// One line per log type under the list: what it sends (+ the Splunk
// sourcetype on the Splunk form); the undelivered ones on a closing line.
function LogTypeHelp({ options, showSourcetype }) {
  const live = options.filter((o) => LOG_TYPE_INFO[o] && !isComingSoon(o));
  const soon = options.filter(isComingSoon);
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: "0.25rem" }}>
      {live.map((o) => (
        <li key={o}>
          <span style={{ fontWeight: 600, color: AN_COLORS.ink2 }}>{o}</span>: {LOG_TYPE_INFO[o].sends}
          {showSourcetype && (
            <>
              {". Splunk sourcetype "}
              <code style={{ fontSize: "0.6875rem", padding: "0 0.25rem", borderRadius: "0.25rem", background: AN_COLORS.bg, border: `1px solid ${AN_COLORS.line}` }}>
                {LOG_TYPE_INFO[o].sourcetype}
              </code>
            </>
          )}
          .
        </li>
      ))}
      {soon.length > 0 && (
        <li>
          <span style={{ fontWeight: 600, color: AN_COLORS.ink2 }}>{soon.join(", ")}</span>: not delivered yet (coming soon).
        </li>
      )}
    </ul>
  );
}

// Secrets live in the vault (backend commit 5469e3a). getLogOutputConfiguration
// returns SECRET_MASK for a stored secret and "" otherwise; on save, sending
// SECRET_MASK keeps the stored value, "" clears it, anything else replaces it.
// The vault holds one set per tenant: password, authToken, httpPassword (shared
// by Splunk's HTTP Password and Elasticsearch's HTTP Password) and customHeaders.
const SECRET_MASK = "********";
const SECRET_KEYS = ["authToken", "password", "httpPassword", "httpPasswd", "customHeaders"];
// Form key → API (request) field.
const SECRET_API_FIELD = {
  authToken: "authToken",
  password: "password",
  httpPassword: "httpPassword",
  httpPasswd: "httpPassword",
  customHeaders: "customHeaders",
};
// Header names the SIEM renderer accepts (siem headerName in render.go); values
// must be strings and free of line breaks.
const HEADER_NAME_RE = /^[A-Za-z0-9-]{1,64}$/;

// Output types with their configurations
const OUTPUT_CONFIG = {
  Splunk: {
    icon: "https://www.splunk.com/content/dam/splunk2/en_us/images/icon-library/footer/logo-splunk-corp-rgb-k-web.svg ",
    color: "#0070CD",
    title: "Splunk Integration",
    subtitle: "Connect to your Splunk instance via HTTP Event Collector",
    description:
      "Splunk allows you to search, analyze, and visualize machine data in real-time, transforming it into actionable insights.",
    documentationLink: "https://docs.fluentbit.io/manual/pipeline/outputs/splunk",
  },
  Syslog: {
    icon: "https://cdn-icons-png.flaticon.com/512/8888/8888205.png ",
    color: "#00A76F",
    title: "Syslog Integration",
    subtitle: "Stream logs to your Syslog server via UDP or TCP",
    description:
      "Send logs to any standard Syslog server with configurable facilities, severity levels, and transport protocols.",
    documentationLink: "https://docs.fluentbit.io/manual/pipeline/outputs/syslog",
  },
  HTTP: {
    icon: "https://cdn-icons-png.flaticon.com/512/2165/2165004.png",
    color: "#7635DC",
    title: "HTTP Output",
    subtitle: "Send logs to any HTTP/HTTPS endpoint",
    description:
      "Forward logs to web services or custom endpoints with configurable formats, headers, and authentication methods.",
    documentationLink: "https://docs.fluentbit.io/manual/pipeline/outputs/http",
  },
  Elasticsearch: {
    icon: "https://assets.zabbix.com/img/brands/elasticsearch.svg",
    color: "#F6AD37",
    title: "Elasticsearch Output",
    subtitle: "Index logs directly into Elasticsearch",
    description:
      "Store and analyze logs with powerful search capabilities and visualize them with Kibana dashboards.",
    documentationLink: "https://docs.fluentbit.io/manual/pipeline/outputs/elasticsearch",
  },
};

const SPLUNK_PARAMETERS = [
  {
    id: 1,
    label: "Host",
    key: "host",
    required: true,
    tooltip:
      "The hostname or IP address of your Splunk instance where the HTTP Event Collector (HEC) is configured. Example: splunk.company.com",
  },
  {
    id: 2,
    label: "Port",
    key: "port",
    required: true,
    type: "number",
    tooltip:
      "The port number where your Splunk HTTP Event Collector is listening. Default is 8088 for HTTPS and 8089 for HTTP.",
  },
  {
    id: 3,
    label: "Authentication Token",
    key: "authToken",
    required: true,
    tooltip:
      "The HEC token generated in your Splunk instance. This token authenticates and authorizes the connection to send data to Splunk.",
  },
  {
    id: 4,
    label: "Password",
    key: "password",
    required: false,
    type: "password",
    tooltip:
      "The password associated with your Splunk account. This is used for authentication when sending data to Splunk.",
  },
  {
    id: 5,
    label: "HTTP Username",
    key: "httpUsername",
    required: false,
    tooltip:
      "Optional username for HTTP basic authentication if your Splunk instance requires it. This is different from your Splunk login credentials.",
  },
  {
    id: 6,
    label: "HTTP Password",
    key: "httpPassword",
    required: false,
    type: "password",
    tooltip:
      "Optional password for HTTP basic authentication. Only required if HTTP Username is provided.",
  },
  {
    id: 7,
    label: "Log Type",
    key: "logType",
    required: true,
    type: "multiselect",
    options: LOG_TYPES,
    tooltip:
      "Select the type of logs to forward to Splunk. This helps in organizing and filtering logs in your Splunk dashboard.",
  },
];

const HTTP_PARAMETERS = [
  {
    id: 1,
    label: "Host",
    key: "host",
    required: true,
    tooltip:
      "The hostname or IP address of your HTTP endpoint where logs will be sent. Example: logs.api.company.com",
  },
  {
    id: 2,
    label: "Port",
    key: "port",
    required: true,
    type: "number",
    defaultValue: 443,
    tooltip:
      "The port number of your HTTP endpoint. Default is 443 for HTTPS and 80 for HTTP. Make sure this port is accessible and allowed by your firewall.",
  },
  {
    id: 3,
    label: "URI",
    key: "uri",
    required: true,
    defaultValue: "/",
    tooltip:
      "The specific path on your HTTP endpoint where logs should be sent. Example: /api/v1/logs or /ingest",
  },
  {
    id: 4,
    label: "Content Type",
    key: "contentType",
    required: false,
    defaultValue: "application/json",
    tooltip:
      "The Content-Type header value for your HTTP requests. Common values are application/json or application/x-ndjson for newline-delimited JSON.",
  },
  {
    id: 5,
    label: "Custom Headers",
    key: "customHeaders",
    required: false,
    type: "textarea",
    tooltip:
      'Additional HTTP headers in JSON format. Example: {"Authorization": "Bearer token", "X-Custom-Header": "value"}',
  },

  {
    id: 9,
    label: "Use TLS",
    key: "useTls",
    type: "select",
    options: ["true", "false"],
    defaultValue: "true",
    required: false,
    tooltip:
      "Enable TLS for secure HTTPS connections. Strongly recommended for production environments to encrypt data in transit.",
  },
  {
    id: 10,
    label: "TLS Verify",
    key: "tlsVerify",
    type: "select",
    options: ["true", "false"],
    defaultValue: "true",
    required: true,
    tooltip:
      "Verify TLS certificates when making HTTPS connections. Disable only in development with self-signed certificates.",
  },
  {
    id: 11,
    label: "JSON Date Key",
    key: "jsonDateKey",
    required: false,
    defaultValue: "date",
    tooltip:
      "The key name for the timestamp field in your JSON output. Common values: date, timestamp, @timestamp",
  },
  {
    id: 12,
    label: "JSON Date Format",
    key: "jsonDateFormat",
    required: false,
    defaultValue: "iso8601",
    tooltip:
      "Format for timestamp values. iso8601 is recommended for compatibility. Example: 2024-03-21T14:30:00Z",
  },
  {
    id: 13,
    label: "Log Type",
    key: "logType",
    required: true,
    type: "multiselect",
    options: LOG_TYPES,
    tooltip:
      "Select which type of logs to forward to your HTTP endpoint. This helps in organizing and filtering logs at the destination.",
  },
];

const SYSLOG_PARAMETERS = [
  {
    id: 1,
    label: "Host",
    key: "host",
    required: true,
    tooltip:
      "The hostname or IP address of your Syslog server. Ensure the server is configured to accept external Syslog messages.",
  },
  {
    id: 2,
    label: "Port",
    key: "port",
    required: true,
    type: "number",
    defaultValue: 514,
    tooltip:
      "The port your Syslog server is listening on. Standard ports are 514 for UDP/TCP or 6514 for TLS. Verify your firewall allows this port.",
  },
  {
    id: 3,
    label: "Mode",
    key: "mode",
    required: true,
    type: "select",
    options: ["TCP", "UDP", "TLS"],
    defaultValue: "TCP",
    tooltip:
      "Transport protocol for sending logs. TCP ensures reliable delivery but may be slower, UDP is faster but messages could be lost.",
  },
  {
    id: 4,
    label: "Syslog Format",
    key: "syslogFormat",
    required: false,
    defaultValue: "rfc3164",
    tooltip:
      "Syslog message format specification. RFC3164 is the traditional format, RFC5424 is newer with structured data support.",
  },
  {
    id: 5,
    label: "Message Key",
    key: "messageKey",
    required: true,
    defaultValue: "message",
    tooltip:
      "The field name in your log data that contains the main message content to be sent to Syslog.",
  },

  {
    id: 10,
    label: "TLS Verify",
    key: "tlsVerify",
    type: "select",
    options: ["true", "false"],
    defaultValue: "true",
    required: true,
    tooltip:
      "Verify TLS certificates when using secure Syslog (TCP with TLS). Disable only in development with self-signed certificates.",
  },
  {
    id: 11,
    label: "Log Type",
    key: "logType",
    required: true,
    type: "multiselect",
    options: LOG_TYPES,
    tooltip:
      "Select which type of logs to forward to Syslog. This helps in organizing and filtering logs at the destination.",
  },
];

const ELASTICSEARCH_PARAMETERS = [
  {
    id: 1,
    label: "Host",
    key: "host",
    required: true,
    tooltip:
      "The hostname or IP address of your Elasticsearch server. For clusters, specify the load balancer or primary node address.",
  },
  {
    id: 2,
    label: "Port",
    key: "port",
    required: true,
    type: "number",
    defaultValue: 9200,
    tooltip:
      "The HTTP port of your Elasticsearch server. Default is 9200 for REST API. For secure clusters, 9243 is common.",
  },
  {
    id: 3,
    label: "Index Name",
    key: "indexName",
    required: true,
    tooltip:
      "The Elasticsearch index name where logs will be stored. Can include date patterns like logs-YYYY.MM.DD for rotation. Example: app-logs-*",
  },
  {
    id: 4,
    label: "HTTP Username",
    key: "httpUser",
    required: false,
    tooltip:
      "Username for Elasticsearch basic authentication. Required if your cluster uses security features like X-Pack.",
  },
  {
    id: 5,
    label: "HTTP Password",
    key: "httpPasswd",
    required: false,
    type: "password",
    tooltip: "Password for Elasticsearch basic authentication. Required if username is provided.",
  },
  {
    id: 6,
    label: "Use TLS",
    key: "tls",
    type: "select",
    options: ["true", "false"],
    defaultValue: "true",
    required: false,
    tooltip:
      "Enable TLS for secure HTTPS connections to Elasticsearch. Strongly recommended for production environments.",
  },
  {
    id: 7,
    label: "TLS Verify",
    key: "tlsVerify",
    type: "select",
    options: ["true", "false"],
    defaultValue: "true",
    required: true,
    tooltip:
      "Verify TLS certificates when connecting to Elasticsearch. Disable only in development with self-signed certificates.",
  },
  {
    id: 8,
    label: "Log Type",
    key: "logType",
    required: true,
    type: "multiselect",
    options: LOG_TYPES,
    tooltip:
      "Select which type of logs to forward to Elasticsearch. This helps in organizing and querying logs within your indices.",
  },
];

export default function LogsOutputConfigForm() {
  const [form] = Form.useForm();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const outputType = queryParams.get("op") || "Splunk";
  const [loading, setLoading] = useState(false);
  const [existingConfig, setExistingConfig] = useState(null);
  const [fetchingConfig, setFetchingConfig] = useState(true);
  // Secret fields the server reports as stored (came back as SECRET_MASK), and
  // the ones the user has explicitly chosen to clear on save.
  const [storedSecrets, setStoredSecrets] = useState({});
  const [clearedSecrets, setClearedSecrets] = useState({});

  // Get metadata for current output type
  const outputConfig = OUTPUT_CONFIG[outputType] || {
    color: "#666",
    title: `${outputType} Configuration`,
    subtitle: "Configure connection parameters",
    description: "Provide the connection details to integrate with your log destination.",
  };

  // Fetch existing configurations
  useEffect(() => {
    setFetchingConfig(true);

    axios
      .post(
        `${REACT_APP_API_TENANTS_URL}/getLogOutputConfiguration`,
        {
          orgId: ORG_ID(),
          tenantId: DOMAIN_ID(),
        },
        {
          headers: {
            withCredentials: true,
            "X-Authorization": AUTH_TOKEN(),
          },
        }
      )
      .then((response) => {
        setFetchingConfig(false);
        if (response.data && response.data.code !== 500) {
          setExistingConfig(response.data);

          // Populate form with existing values based on output type
          const configKey = getOutputTypeKey(outputType);
          if (response.data[configKey]) {
            const config = response.data[configKey];
            const formValues = {};
            const stored = {};

            // Map config values to form fields. The row structs have no json
            // tags, so keys arrive as Go field names (Host, AuthToken, …).
            Object.keys(config).forEach((key) => {
              // Convert key to camelCase if needed
              const formKey = key.charAt(0).toLowerCase() + key.slice(1);
              // A stored secret comes back masked: never put the mask in the
              // input, show it as a "stored" placeholder instead.
              if (SECRET_KEYS.includes(formKey)) {
                if (config[key] === SECRET_MASK) stored[formKey] = true;
                else if (config[key]) formValues[formKey] = config[key];
                return;
              }
              if (config[key] !== 0 && config[key] !== "") {
                formValues[formKey] = config[key];
              }

              // Handle boolean values
              if (typeof config[key] === "boolean") {
                formValues[formKey] = config[key].toString();
              }
            });

            // Log types are stored comma-joined (Splunk's column is "Logtype",
            // the others "LogType").
            const types = config.LogType || config.Logtype || "";
            delete formValues.logtype;
            formValues.logType = String(types)
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean)
              .map((t) => LOG_TYPES.find((o) => o.toLowerCase() === t.toLowerCase()) || t);

            setStoredSecrets(stored);
            setClearedSecrets({});
            form.setFieldsValue(formValues);
          }
        }
      })
      .catch((error) => {
        setFetchingConfig(false);
        console.error("Error fetching log configurations:", error?.response?.status, error?.message);
      });
  }, [outputType, form]);

  // Function to get correct case mapping for output type
  const getOutputTypeKey = (type) => {
    switch (type) {
      case "Splunk":
        return "splunk";
      case "HTTP":
        return "http";
      case "Syslog":
        return "syslog";
      case "Elasticsearch":
        return "elasticsearch";
      default:
        return type.toLowerCase();
    }
  };

  // Function to get correct case for output type
  const getCorrectOutputName = (type) => {
    switch (type) {
      case "Splunk":
        return "Splunk";
      case "HTTP":
        return "Http";
      case "Syslog":
        return "Syslog";
      case "Elasticsearch":
        return "ElasticSearch";
      default:
        return type;
    }
  };

  // What to send for a secret field: the typed value; else the mask (keep) when
  // one is stored and not cleared; else "" (clear / none).
  const secretValue = (values, formKey) => {
    const typed = values[formKey];
    if (typeof typed === "string" && typed !== "") return typed;
    if (storedSecrets[formKey] && !clearedSecrets[formKey]) return SECRET_MASK;
    return "";
  };

  const onFinish = (values) => {
    // Secret fields this output's form shows; the others are sent as "" so a
    // switch of output retires the previous output's secrets.
    const shownKeys = getParametersForOutputType().map((p) => p.key);
    const secrets = { password: "", authToken: "", httpPassword: "", customHeaders: "" };
    SECRET_KEYS.forEach((k) => {
      if (shownKeys.includes(k)) secrets[SECRET_API_FIELD[k]] = secretValue(values, k);
    });
    if (typeof secrets.customHeaders === "string" && secrets.customHeaders !== SECRET_MASK) {
      secrets.customHeaders = secrets.customHeaders.trim();
    }

    setLoading(true);

    // Transform form values to match API payload structure
    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      outputName: getCorrectOutputName(outputType),
      host: values.host,
      uri: values.uri,
      httpMethod: "POST",
      useTls: values.useTls === "true" || values.tls === "true",
      tlsVerify: values.tlsVerify === "true",
      // A JSON string, not an object: the DTO field is a string and an object
      // fails binding with 400. Validated as a JSON object by the form rule.
      customHeaders: secrets.customHeaders,
      jsonDateFormat: values.jsonDateFormat,
      jsonDateKey: values.jsonDateKey,
      indexName: values.indexName,
      syslogFormat: values.syslogFormat,
      messageKey: values.messageKey,
      messagePattern: values.messagePattern,
      mode: values.mode,
      password: secrets.password,
      authToken: secrets.authToken,
      httpUsername: values.httpUsername || values.httpUser,
      httpPassword: secrets.httpPassword,
      port: parseInt(values.port, 10),

      workers: values.workers ? parseInt(values.workers, 10) : undefined,
      matchPattern: values.matchPattern,
      typesOfLogs: Array.isArray(values.logType) ? values.logType : values.logType ? [values.logType] : [],
    };

    // Determine if we're updating or creating a new config
    const endpoint = hasExistingConfig()
      ? `${REACT_APP_API_TENANTS_URL}/updateLogOutputConfiguration`
      : `${REACT_APP_API_TENANTS_URL}/storeLogOutputConfiguration`;

    axios
      .post(endpoint, payload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      })
      .then((response) => {
        setLoading(false);
        if (response.data && response.data.status === "success") {
          message.success(
            `${outputType} output configuration ${
              hasExistingConfig() ? "updated" : "saved"
            } successfully!`
          );
          // Navigate back to the main logs config page after a short delay
          setTimeout(() => {
            window.location.href = "/pam/settings/logsConfig";
          }, 1500);
        } else if (response.data.code === 500) {
          message.error(response.data.message || "Not able to process log output configuration");
        } else {
          message.error(response.data.message || "Failed to save configuration");
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error saving log configuration:", error?.response?.status, error?.message);

        if (error.response && error.response.status === 401) {
          message.error("Authentication error. Please log in again.");
        } else {
          message.error("Failed to save configuration. Please try again.");
        }
      });
  };

  const getParametersForOutputType = () => {
    switch (outputType) {
      case "Splunk":
        return SPLUNK_PARAMETERS;
      case "HTTP":
        return HTTP_PARAMETERS;
      case "Syslog":
        return SYSLOG_PARAMETERS;
      case "Elasticsearch":
        return ELASTICSEARCH_PARAMETERS;
      default:
        return SPLUNK_PARAMETERS;
    }
  };

  const getDescription = () => {
    switch (outputType) {
      case "Splunk":
        return "Configure your Splunk integration by providing the required connection details below. Mandatory fields include Host, Port, and Splunk Token. Optionally specify HTTP credentials for additional authentication.";
      case "HTTP":
        return "Configure your HTTP output by providing the endpoint details below. Mandatory fields include Host, Port, and URI. Optionally specify authentication credentials and output format.";
      case "Syslog":
        return "Configure your Syslog output by providing server details below. Mandatory fields include Host, Port, and Mode. Optionally specify Syslog Facility and Severity levels.";
      case "Elasticsearch":
        return "Configure your Elasticsearch integration by providing the required connection details below. Mandatory fields include Host, Port, and Index. Optionally specify HTTP credentials and Logstash format settings.";
      default:
        return "Configure your output integration by providing the required connection details below.";
    }
  };

  const isStored = (key) => !!storedSecrets[key] && !clearedSecrets[key];

  const secretPlaceholder = (param) => {
    if (!SECRET_KEYS.includes(param.key)) return null;
    if (isStored(param.key)) return `${SECRET_MASK} (stored) — leave empty to keep`;
    if (storedSecrets[param.key]) return "Will be cleared on save";
    return null;
  };

  // "Clear" / "Undo" under a stored secret. Leaving the input empty keeps the
  // stored value; clearing is explicit.
  const secretExtra = (param) => {
    if (!SECRET_KEYS.includes(param.key) || !storedSecrets[param.key]) return null;
    const cleared = !!clearedSecrets[param.key];
    const toggle = (e) => {
      e.preventDefault();
      setClearedSecrets((c) => ({ ...c, [param.key]: !cleared }));
      if (!cleared) form.setFieldsValue({ [param.key]: "" });
      // Re-run the required rule: clearing a required stored secret makes it required.
      setTimeout(() => form.validateFields([param.key]).catch(() => {}), 0);
    };
    return (
      <span style={{ fontSize: 12, color: AN_COLORS.muted }}>
        {cleared ? "The stored value will be removed on save. " : "A value is stored. Type to replace it, or "}
        <a href="#clear" onClick={toggle} style={{ fontWeight: 500 }}>
          {cleared ? "Undo" : "clear it"}
        </a>
        {cleared ? "" : "."}
      </span>
    );
  };

  const paramRules = (param) => {
    const rules = [
      {
        // A stored secret satisfies "required" until it is cleared.
        required: param.required && !(SECRET_KEYS.includes(param.key) && isStored(param.key)),
        message: `${param.label} is required`,
      },
    ];
    if (param.type === "multiselect" && param.required) {
      rules[0].type = "array";
      rules[0].min = 1;
      rules[0].message = `Select at least one ${param.label.toLowerCase()}`;
    }
    if (param.key === "customHeaders") {
      rules.push({
        validator: (_, value) => {
          if (!value || !String(value).trim()) return Promise.resolve();
          let parsed;
          try {
            parsed = JSON.parse(value);
          } catch (_) {
            return Promise.reject(new Error('Must be valid JSON, e.g. {"Authorization": "Bearer …"}'));
          }
          if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return Promise.reject(new Error("Must be a JSON object of header names to values"));
          }
          for (const [name, v] of Object.entries(parsed)) {
            if (!HEADER_NAME_RE.test(name)) {
              return Promise.reject(new Error(`Header name "${name}" may only hold letters, digits and hyphens`));
            }
            if (typeof v !== "string") {
              return Promise.reject(new Error(`Header "${name}" must have a string value`));
            }
            if (/[\r\n\u0000-\u001f]/.test(v)) {
              return Promise.reject(new Error(`Header "${name}" must not contain line breaks`));
            }
          }
          return Promise.resolve();
        },
      });
    }
    return rules;
  };

  // Check if configuration exists for the current output type
  const hasExistingConfig = () => {
    if (!existingConfig) return false;

    const config = existingConfig[getOutputTypeKey(outputType)];
    if (!config) return false;

    // Check if at least host and port are defined
    return config.Host && config.Port;
  };

  // One antd Form.Item per parameter, inside a SettingsField row: the field row
  // carries the label (+ tooltip), the Form.Item keeps the rules, the error
  // and the stored-secret note.
  const renderParam = (param, last) => (
    <SettingsField
      key={param.id}
      last={last}
      required={!!paramRules(param)[0].required}
      label={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {param.label}
          {param.tooltip && (
            <Tooltip title={param.tooltip} placement="right">
              <InfoCircleOutlined style={{ color: AN_COLORS.muted, fontSize: 12, cursor: "help" }} />
            </Tooltip>
          )}
        </span>
      }
    >
      <Form.Item name={param.key} rules={paramRules(param)} extra={secretExtra(param)} style={{ marginBottom: 0 }}>
        {param.type === "multiselect" ? (
          <Select
            mode="multiple"
            allowClear
            placeholder={`Select ${param.label.toLowerCase()}s`}
            options={param.key === "logType" ? logTypeOptions(param.options) : param.options.map((o) => ({ value: o, label: o }))}
          />
        ) : param.type === "select" ? (
          <Select placeholder={`Select ${param.label.toLowerCase()}`}>
            {param.options.map((option) => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        ) : param.type === "textarea" ? (
          <Input.TextArea
            placeholder={secretPlaceholder(param) || param.placeholder || `Enter ${param.label.toLowerCase()}`}
            rows={4}
          />
        ) : (
          <Input
            type={param.type === "password" || param.key.includes("password") ? "password" : "text"}
            placeholder={secretPlaceholder(param) || param.placeholder || `Enter ${param.label.toLowerCase()}`}
            autoComplete={SECRET_KEYS.includes(param.key) ? "new-password" : undefined}
          />
        )}
      </Form.Item>
      {param.type === "multiselect" && param.key === "logType" && (
        <div style={{ fontSize: "0.75rem", marginTop: "0.5rem", color: AN_COLORS.muted, lineHeight: 1.5 }}>
          <LogTypeHelp options={param.options} showSourcetype={outputType === "Splunk"} />
        </div>
      )}
    </SettingsField>
  );

  const params = getParametersForOutputType();
  const connectionParams = params.filter((p) => p.type !== "multiselect");
  const logTypeParams = params.filter((p) => p.type === "multiselect");
  const configured = !!hasExistingConfig();

  return (
    <SettingsPage
      title={outputConfig.title}
      description={`${configured ? `Edit your existing ${outputConfig.subtitle}` : outputConfig.subtitle}. ${outputConfig.description}`}
    >
      {fetchingConfig ? (
        <div style={{ padding: 60, textAlign: "center" }}>
          <Spin tip="Loading configuration...">
            <div style={{ minHeight: 40 }} />
          </Spin>
        </div>
      ) : (
        <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off" style={{ display: "grid", gap: 24 }}>
          <SettingsSection
            title="Connection details"
            description={getDescription()}
            actions={
              <SettingsStatus tone={configured ? "success" : "neutral"}>
                {configured ? "Configured" : "Not configured"}
              </SettingsStatus>
            }
          >
            {connectionParams.map((param, i) => renderParam(param, i === connectionParams.length - 1))}
          </SettingsSection>

          {logTypeParams.length > 0 && (
            <SettingsSection title="Log types" description={`The logs this tenant forwards to ${outputType}.`}>
              {logTypeParams.map((param, i) => renderParam(param, i === logTypeParams.length - 1))}
            </SettingsSection>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => navigate("/pam/settings/logsConfig")}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {loading ? "Saving..." : configured ? "Update configuration" : "Save configuration"}
            </Button>
          </div>
        </Form>
      )}
    </SettingsPage>
  );
}
